import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { AlertTriangle, CheckCircle2, Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', green: '#1D9E75', red: '#E24B4A', amber: '#E8A020', slate: '#5C6B7A', bg: '#F5F7FA' };
const MIN_NOTE = 10;

const ClearForm = ({ sub, rule, api, onCleared, onCancel }) => {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const id = `${sub.submission_id}-${rule}`;
  const ready = note.trim().length >= MIN_NOTE;
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/submissions/${sub.submission_id}/overrides/${rule}/clear`, { note: note.trim() });
      toast.success(data.all_cleared ? `${rule} cleared — every referral reason is now cleared; approve to finish` : `${rule} cleared and recorded on the file`);
      onCleared();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not clear the referral reason');
    }
    setBusy(false);
  };
  return (
    <div className="mt-1.5 ml-5 p-2.5 rounded-md" style={{ background: C.bg }} data-testid={`clear-form-${id}`}>
      <p className="text-[11px] font-medium mb-1.5" style={{ color: C.navy }}>Why is this referral reason cleared? It is recorded on the file and in the audit trail.</p>
      <textarea value={note} onChange={e => setNote(e.target.value)} data-testid={`clear-note-${id}`}
        placeholder="E.g. stock and plant stored above the 1% AEP level; broker confirmed flood excess accepted…"
        className="w-full px-2.5 py-1.5 border rounded-md text-xs h-16 resize-none bg-white" />
      <div className="flex items-center gap-2 mt-1.5">
        <Button size="sm" onClick={submit} disabled={!ready || busy} data-testid={`clear-confirm-${id}`}
          className="rounded-md text-xs h-7" style={{ background: C.green }}>
          {busy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <ShieldCheck className="w-3 h-3 mr-1" />} Clear {rule}
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel} className="rounded-md text-xs h-7" data-testid={`clear-cancel-${id}`}>Cancel</Button>
        <span className="text-[10px]" style={{ color: ready ? C.green : C.slate }} data-testid={`clear-note-count-${id}`}>
          {note.trim().length}/{MIN_NOTE} characters minimum
        </span>
      </div>
    </div>
  );
};

export const OverrideReasons = ({ sub, api, onChanged, canClear = false }) => {
  const [openForm, setOpenForm] = useState(null);
  const rules = sub.scoring_result?.override_rules_triggered || [];
  if (!rules.length) return null;
  const referrals = rules.filter(r => r.action === 'AUTO_REFER');
  const open = referrals.filter(r => !r.cleared);
  return (
    <div className="mt-2 space-y-1.5" data-testid={`override-reasons-${sub.submission_id}`}>
      {rules.map((r, i) => {
        const key = r.rule || String(i);
        const id = `${sub.submission_id}-${key}`;
        return (
          <div key={key} data-testid={`override-${id}`}>
            <div className="flex items-start gap-2 text-xs">
              {r.cleared
                ? <CheckCircle2 className="w-3 h-3 mt-0.5 shrink-0" style={{ color: C.green }} />
                : <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" style={{ color: C.amber }} />}
              <span style={{ color: C.navy, textDecoration: r.cleared ? 'line-through' : 'none', opacity: r.cleared ? 0.7 : 1 }}>{r.desc}</span>
              {canClear && r.action === 'AUTO_REFER' && !r.cleared && (
                <button onClick={() => setOpenForm(openForm === key ? null : key)} data-testid={`clear-reason-${id}`}
                  className="ml-auto shrink-0 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-sm"
                  style={{ background: 'rgba(26,76,124,0.08)', color: C.blue }}>
                  Clear this reason
                </button>
              )}
            </div>
            {r.citation && (
              <p className="ml-5 mt-0.5 font-mono text-[10px] leading-snug break-words" style={{ color: C.slate }} data-testid={`citation-${id}`}>
                {r.citation}
              </p>
            )}
            {r.cleared && (
              <p className="ml-5 mt-0.5 text-[10px]" style={{ color: C.green }} data-testid={`cleared-${id}`}>
                Cleared by {r.cleared.by_name} · {new Date(r.cleared.at).toLocaleDateString('en-AU')} — <span style={{ color: C.navy }}>{r.cleared.note}</span>
              </p>
            )}
            {openForm === key && (
              <ClearForm sub={sub} rule={key} api={api} onCancel={() => setOpenForm(null)}
                onCleared={() => { setOpenForm(null); onChanged?.(); }} />
            )}
          </div>
        );
      })}
      {referrals.length > 0 && open.length === 0 && (
        <div className="flex items-center gap-2 p-2 rounded-md text-xs font-medium" data-testid={`all-cleared-${sub.submission_id}`}
          style={{ background: 'rgba(29,158,117,0.08)', color: C.green }}>
          <CheckCircle2 className="w-3.5 h-3.5" /> All referral reasons cleared — Approve &amp; Bind or Approve with Conditions to finish.
        </div>
      )}
    </div>
  );
};

export default OverrideReasons;
